import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  RefreshCw, 
  Users, 
  Calendar,
  MessageSquare,
  Save,
  X,
  Phone,
  MapPin,
  Bike,
  Car,
  Utensils,
  GraduationCap,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FilterX
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
} from "@/components/ui/card";

export const RiderManagement = () => {
  const { user } = useAuth();
  const [riders, setRiders] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Filters - default to empty/none
  const [cityFilter, setCityFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [trainingFilter, setTrainingFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [followUpFrom, setFollowUpFrom] = useState("");
  const [followUpTo, setFollowUpTo] = useState("");
  const [registeredFrom, setRegisteredFrom] = useState("");
  const [registeredTo, setRegisteredTo] = useState("");
  
  // Sorting
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [editForm, setEditForm] = useState({
    service_status: "",
    training_status: "",
    start_date: "",
    follow_up_date: "",
    admin_comments: ""
  });
  const [saving, setSaving] = useState(false);
  
  // Filters panel visibility
  const [showFilters, setShowFilters] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch riders
  const fetchRiders = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (cityFilter) params.append("city_id", cityFilter);
      if (serviceFilter) params.append("service_status", serviceFilter);
      if (trainingFilter) params.append("training_status", trainingFilter);
      if (statusFilter) params.append("approval_status", statusFilter);
      if (followUpFrom) params.append("follow_up_from", followUpFrom);
      if (followUpTo) params.append("follow_up_to", followUpTo);
      if (registeredFrom) params.append("registered_from", registeredFrom);
      if (registeredTo) params.append("registered_to", registeredTo);
      
      const response = await axios.get(
        `${API}/admin/${user.id}/riders/management?${params.toString()}`
      );
      
      setRiders(response.data.riders || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
      
      // Set cities from response
      if (response.data.cities) {
        setCities(response.data.cities);
      }
    } catch (error) {
      console.error("Error fetching riders:", error);
      toast.error("Failed to fetch riders");
    } finally {
      setLoading(false);
    }
  }, [user?.id, page, limit, debouncedSearch, cityFilter, serviceFilter, trainingFilter, statusFilter, followUpFrom, followUpTo, registeredFrom, registeredTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  // Clear all filters
  const clearFilters = () => {
    setCityFilter("");
    setServiceFilter("");
    setTrainingFilter("");
    setStatusFilter("");
    setFollowUpFrom("");
    setFollowUpTo("");
    setRegisteredFrom("");
    setRegisteredTo("");
    setSearchInput("");
    setPage(1);
  };

  // Check if any filter is active
  const hasActiveFilters = cityFilter || serviceFilter || trainingFilter || statusFilter || 
    followUpFrom || followUpTo || registeredFrom || registeredTo || searchInput;

  // Handle column sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(1);
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortOrder === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  // Open edit dialog
  const openEditDialog = (rider) => {
    setSelectedRider(rider);
    setEditForm({
      service_status: rider.service_status || "",
      training_status: rider.training_status || "",
      start_date: rider.start_date ? rider.start_date.split('T')[0] : "",
      follow_up_date: rider.follow_up_date ? rider.follow_up_date.split('T')[0] : "",
      admin_comments: rider.admin_comments || ""
    });
    setShowEditDialog(true);
  };

  // Save rider details
  const saveRiderDetails = async () => {
    if (!selectedRider) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/${user.id}/riders/${selectedRider.id}/management`,
        editForm
      );
      toast.success("Rider details updated successfully");
      setShowEditDialog(false);
      fetchRiders();
    } catch (error) {
      console.error("Error updating rider:", error);
      toast.error(error.response?.data?.detail || "Failed to update rider");
    } finally {
      setSaving(false);
    }
  };

  // Get service status badge
  const getServiceBadge = (status) => {
    switch (status) {
      case "taxi_only":
        return <Badge className="bg-blue-100 text-blue-700">Taxi Only</Badge>;
      case "taxi_and_food":
        return <Badge className="bg-green-100 text-green-700">Taxi + Food</Badge>;
      case "food_only":
        return <Badge className="bg-orange-100 text-orange-700">Food Only</Badge>;
      case "inactive":
        return <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-500">Not Set</Badge>;
    }
  };

  // Get training status badge
  const getTrainingBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case "in_progress":
        return <Badge className="bg-yellow-100 text-yellow-700">In Progress</Badge>;
      case "scheduled":
        return <Badge className="bg-blue-100 text-blue-700">Scheduled</Badge>;
      case "not_started":
        return <Badge className="bg-gray-100 text-gray-500">Not Started</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-500">Not Set</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Export to CSV - fetches ALL data, not just current page
  const exportToCSV = async () => {
    try {
      toast.info("Fetching all data for export...");
      
      // Fetch ALL riders without pagination
      const params = new URLSearchParams({
        page: "1",
        limit: "10000",  // Large limit to get all
      });
      
      // Apply current filters
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (cityFilter) params.append("city_id", cityFilter);
      if (serviceFilter) params.append("service_status", serviceFilter);
      if (trainingFilter) params.append("training_status", trainingFilter);
      if (statusFilter) params.append("approval_status", statusFilter);
      if (followUpFrom) params.append("follow_up_from", followUpFrom);
      if (followUpTo) params.append("follow_up_to", followUpTo);
      if (registeredFrom) params.append("registered_from", registeredFrom);
      if (registeredTo) params.append("registered_to", registeredTo);
      
      const response = await axios.get(
        `${API}/admin/${user.id}/riders/management?${params.toString()}`
      );
      
      const allRiders = response.data.riders || [];
      
      const headers = ["Name", "Phone", "City", "Vehicle", "Service Status", "Training Status", "Start Date", "Follow Up Date", "Registered Date", "Comments"];
      const rows = allRiders.map(r => [
        r.name,
        r.phone,
        r.city_name || "-",
        r.vehicle_type,
        r.service_status || "Not Set",
        r.training_status || "Not Started",
        r.start_date ? formatDate(r.start_date) : "-",
        r.follow_up_date ? formatDate(r.follow_up_date) : "-",
        formatDate(r.created_at),
        (r.admin_comments || "").replace(/,/g, ";")
      ]);
      
      const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `riders_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${allRiders.length} riders`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export data");
    }
  };

  return (
    <div className="space-y-6" data-testid="rider-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Rider Management</h2>
            <p className="text-sm text-gray-500">Manage rider details, training, and service status</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={fetchRiders} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-gray-500">Total Riders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <GraduationCap className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {riders.filter(r => r.training_status === "completed").length}
                </p>
                <p className="text-xs text-gray-500">Training Done</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Utensils className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {riders.filter(r => r.service_status === "taxi_and_food" || r.service_status === "food_only").length}
                </p>
                <p className="text-xs text-gray-500">Food Delivery</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {riders.filter(r => r.follow_up_date && new Date(r.follow_up_date) <= new Date()).length}
                </p>
                <p className="text-xs text-gray-500">Follow Up Due</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Toggle */}
      <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by name or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
        
        <Button 
          variant={showFilters ? "default" : "outline"} 
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
            <FilterX className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        )}
      </div>

      {/* Expandable Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* City Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">City</Label>
              <Select value={cityFilter || "all"} onValueChange={(v) => { setCityFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="city-filter">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {[...cities].sort((a, b) => a.name.localeCompare(b.name)).map(city => (
                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Service Status</Label>
              <Select value={serviceFilter || "all"} onValueChange={(v) => { setServiceFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="service-filter">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  <SelectItem value="taxi_only">Taxi Only</SelectItem>
                  <SelectItem value="taxi_and_food">Taxi + Food</SelectItem>
                  <SelectItem value="food_only">Food Only</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Training Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Training Status</Label>
              <Select value={trainingFilter || "all"} onValueChange={(v) => { setTrainingFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="training-filter">
                  <SelectValue placeholder="All Training" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Training</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Approval Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Approval Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Follow Up From</Label>
              <Input
                type="date"
                value={followUpFrom}
                onChange={(e) => { setFollowUpFrom(e.target.value); setPage(1); }}
                data-testid="follow-up-from"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Follow Up To</Label>
              <Input
                type="date"
                value={followUpTo}
                onChange={(e) => { setFollowUpTo(e.target.value); setPage(1); }}
                data-testid="follow-up-to"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Registered From</Label>
              <Input
                type="date"
                value={registeredFrom}
                onChange={(e) => { setRegisteredFrom(e.target.value); setPage(1); }}
                data-testid="registered-from"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Registered To</Label>
              <Input
                type="date"
                value={registeredTo}
                onChange={(e) => { setRegisteredTo(e.target.value); setPage(1); }}
                data-testid="registered-to"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center">
                  Rider
                  {getSortIcon("name")}
                </div>
              </TableHead>
              <TableHead className="font-semibold">City</TableHead>
              <TableHead className="font-semibold">Vehicle</TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("service_status")}
              >
                <div className="flex items-center">
                  Service Status
                  {getSortIcon("service_status")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("training_status")}
              >
                <div className="flex items-center">
                  Training
                  {getSortIcon("training_status")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("start_date")}
              >
                <div className="flex items-center">
                  Start Date
                  {getSortIcon("start_date")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("follow_up_date")}
              >
                <div className="flex items-center">
                  Follow Up
                  {getSortIcon("follow_up_date")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center">
                  Registered
                  {getSortIcon("created_at")}
                </div>
              </TableHead>
              <TableHead className="font-semibold">Comments</TableHead>
              <TableHead className="font-semibold text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Loading riders...</p>
                </TableCell>
              </TableRow>
            ) : riders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="text-gray-500 mt-2">No riders found</p>
                </TableCell>
              </TableRow>
            ) : (
              riders.map((rider) => (
                <TableRow 
                  key={rider.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openEditDialog(rider)}
                  data-testid={`rider-row-${rider.id}`}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{rider.name}</span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {rider.phone}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-gray-600">
                      <MapPin className="w-3 h-3" />
                      <span className="text-sm">{rider.city_name || "-"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {rider.vehicle_type === "bike" ? (
                        <Bike className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Car className="w-4 h-4 text-gray-500" />
                      )}
                      <span className="capitalize">{rider.vehicle_type || "-"}</span>
                    </div>
                  </TableCell>
                  <TableCell>{getServiceBadge(rider.service_status)}</TableCell>
                  <TableCell>{getTrainingBadge(rider.training_status)}</TableCell>
                  <TableCell>
                    <span className={rider.start_date ? "text-gray-900" : "text-gray-400"}>
                      {formatDate(rider.start_date)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={
                      rider.follow_up_date && new Date(rider.follow_up_date) <= new Date()
                        ? "text-red-600 font-medium"
                        : rider.follow_up_date
                        ? "text-gray-900"
                        : "text-gray-400"
                    }>
                      {formatDate(rider.follow_up_date)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-600">{formatDate(rider.created_at)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[150px] truncate text-gray-500 text-sm">
                      {rider.admin_comments || "-"}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openEditDialog(rider); }}
                      data-testid={`edit-rider-${rider.id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} riders
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              data-testid="page-first"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 py-1 bg-gray-100 rounded text-sm">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              data-testid="page-last"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Edit Rider Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedRider && (
            <div className="space-y-4">
              {/* Rider Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-purple-600">
                      {selectedRider.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedRider.name}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {selectedRider.phone}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Badge variant="outline" className="capitalize">
                    {selectedRider.vehicle_type || "No vehicle"}
                  </Badge>
                  {selectedRider.city_name && (
                    <Badge variant="outline">
                      <MapPin className="w-3 h-3 mr-1" />
                      {selectedRider.city_name}
                    </Badge>
                  )}
                  <Badge variant="outline">
                    Registered: {formatDate(selectedRider.created_at)}
                  </Badge>
                </div>
              </div>

              {/* Service Status */}
              <div className="space-y-2">
                <Label>Service Status</Label>
                <Select 
                  value={editForm.service_status} 
                  onValueChange={(v) => setEditForm({...editForm, service_status: v})}
                >
                  <SelectTrigger data-testid="edit-service-status">
                    <SelectValue placeholder="Select service status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taxi_only">Taxi Only</SelectItem>
                    <SelectItem value="taxi_and_food">Taxi + Food Delivery</SelectItem>
                    <SelectItem value="food_only">Food Delivery Only</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Training Status */}
              <div className="space-y-2">
                <Label>Training Status</Label>
                <Select 
                  value={editForm.training_status} 
                  onValueChange={(v) => setEditForm({...editForm, training_status: v})}
                >
                  <SelectTrigger data-testid="edit-training-status">
                    <SelectValue placeholder="Select training status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) => setEditForm({...editForm, start_date: e.target.value})}
                    data-testid="edit-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Follow Up Date</Label>
                  <Input
                    type="date"
                    value={editForm.follow_up_date}
                    onChange={(e) => setEditForm({...editForm, follow_up_date: e.target.value})}
                    data-testid="edit-follow-up-date"
                  />
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <Label>Admin Comments</Label>
                <Textarea
                  value={editForm.admin_comments}
                  onChange={(e) => setEditForm({...editForm, admin_comments: e.target.value})}
                  placeholder="Add notes about this rider..."
                  rows={3}
                  data-testid="edit-comments"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={saveRiderDetails} disabled={saving} data-testid="save-rider-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiderManagement;
